import axios from 'axios';
import type {
  ApiResponse,
  AnalyzeResponse,
  ChapterPlanMode,
  ChapterFacts,
  GenerateResponse,
  GenerateOutlineRequest,
  PromptConfig,
  RuleSet,
} from '../../../shared/types';
import { useAuthStore } from '../store/auth';

// 默认端点
export const DEFAULT_API_URL = import.meta.env.VITE_API_BASE || 'https://novel-outline-api.yaoyouwei7.workers.dev';

// 设置存储键
const SETTINGS_KEY = 'novel-outline-settings';
export const ENDPOINT_KEY = 'novel-outline-endpoint';

// 获取用户配置的端点
export function getApiBaseUrl(): string {
  const stored = localStorage.getItem(ENDPOINT_KEY);
  return stored || DEFAULT_API_URL;
}

// 获取用户的 LLM 配置
interface LLMSettings {
  apiKeys: {
    openai?: string;
    glm?: string;
    minimax?: string;
    [key: string]: string | undefined;
  };
  defaultBackend: string;
  apiEndpoint?: string;
}

function getLLMConfig(): { backend: string; apiKey: string; baseUrl?: string; model?: string } | null {
  const saved = localStorage.getItem(SETTINGS_KEY);
  if (!saved) return null;

  try {
    const settings = JSON.parse(saved) as LLMSettings;
    const backend = settings.defaultBackend;
    const apiKey = settings.apiKeys[backend];

    if (!apiKey || !backend) return null;

    const baseUrl = settings.apiKeys[`${backend}BaseUrl`];
    const model = settings.apiKeys[`${backend}Model`];

    return {
      backend,
      apiKey,
      baseUrl: baseUrl as string | undefined,
      model: model as string | undefined,
    };
  } catch {
    return null;
  }
}

function getAuthBaseUrl(): string {
  return getApiBaseUrl();
}

function buildAuthHeaders(): HeadersInit {
  const apiKey = useAuthStore.getState().apiKey;
  return {
    'Content-Type': 'application/json',
    ...(apiKey ? { 'X-API-Key': apiKey } : {}),
  };
}

type FetchWithRetryOptions = {
  timeoutMs: number;
  retries?: number;
  label: string;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getUnknownErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return '';
}

function isTransientFetchError(error: unknown): boolean {
  const message = getUnknownErrorMessage(error).toLowerCase();
  return [
    'fetch is aborted',
    'aborted',
    'aborterror',
    'failed to fetch',
    'networkerror',
    'network request failed',
    'load failed',
    'fetch failed',
  ].some((keyword) => message.includes(keyword));
}

function normalizeFetchError(error: unknown, options: {
  label: string;
  timedOut: boolean;
}): Error {
  const rawMessage = getUnknownErrorMessage(error);

  if (options.timedOut) {
    return new Error(`${options.label}超时，请稍后重试`);
  }

  if (isTransientFetchError(error)) {
    return new Error(`${options.label}被中断，请检查网络后重试`);
  }

  return error instanceof Error
    ? error
    : new Error(rawMessage || `${options.label}失败`);
}

async function fetchWithRetry(
  input: string,
  init: RequestInit,
  options: FetchWithRetryOptions
): Promise<Response> {
  const attempts = Math.max(1, (options.retries ?? 0) + 1);

  for (let attempt = 1; attempt <= attempts; attempt++) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, options.timeoutMs);

    try {
      const response = await fetch(input, {
        ...init,
        signal: controller.signal,
      });
      clearTimeout(timer);
      return response;
    } catch (error) {
      clearTimeout(timer);
      const shouldRetry = attempt < attempts && (timedOut || isTransientFetchError(error));

      if (shouldRetry) {
        await sleep(350 * attempt);
        continue;
      }

      throw normalizeFetchError(error, {
        label: options.label,
        timedOut,
      });
    }
  }

  throw new Error(`${options.label}失败`);
}

async function parseJsonResponse<T>(response: Response, label: string): Promise<T> {
  try {
    return await response.json() as T;
  } catch (error) {
    throw new Error(`${label}返回了无法解析的响应`);
  }
}

// 创建 axios 实例（动态获取 baseURL）
const createApi = () => axios.create({
  baseURL: `${getApiBaseUrl()}/api`,
  timeout: 180000, // 3 分钟，大纲生成需要更长时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// 获取 API 实例
const getApi = () => {
  const api = createApi();

  // 请求拦截器 - 自动添加认证 API Key
  api.interceptors.request.use((config) => {
    const apiKey = useAuthStore.getState().apiKey;
    if (apiKey) {
      config.headers['X-API-Key'] = apiKey;
    }
    return config;
  });

  // 响应拦截器
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      // 优先使用后端返回的错误信息
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.error?.message) {
          return Promise.reject(new Error(errorData.error.message));
        }
        if (errorData.message) {
          return Promise.reject(new Error(errorData.message));
        }
        if (!errorData.success) {
          return Promise.reject(new Error(errorData.error?.message || '请求失败'));
        }
      }
      // 网络错误
      if (error.code === 'ECONNABORTED') {
        return Promise.reject(new Error('请求超时，请重试'));
      }
      if (error.message.includes('Network Error')) {
        return Promise.reject(new Error('网络错误，请检查 API 地址是否正确'));
      }
      return Promise.reject(error);
    }
  );

  return api;
};

// ==================== 认证 API ====================

interface LoginResponse {
  user: {
    id: string;
    email: string;
    name?: string;
    plan: string;
    credits: number;
  };
  apiKeys: Array<{
    id: string;
    name: string;
    prefix: string;
    usage_count: number;
  }>;
}

interface RegisterResponse {
  user: {
    id: string;
    email: string;
    name?: string;
    plan: string;
    credits: number;
  };
  apiKey: string;
  message: string;
}

/**
 * 用户注册
 */
export async function register(email: string, password: string, name?: string): Promise<RegisterResponse> {
  const response = await axios.post<RegisterResponse>(`${getAuthBaseUrl()}/auth/register`, {
    email,
    password,
    name,
  });
  return response.data;
}

/**
 * 用户登录
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await axios.post<LoginResponse>(`${getAuthBaseUrl()}/auth/login`, {
    email,
    password,
  });
  return response.data;
}

/**
 * 获取用户信息
 */
export async function getProfile(): Promise<{ user: LoginResponse['user'] }> {
  const api = getApi();
  const response = await api.get<{ user: LoginResponse['user'] }>('/auth/profile');
  return response.data;
}

/**
 * 获取用户的 API Keys
 */
export async function getApiKeys(): Promise<{ apiKeys: LoginResponse['apiKeys'] }> {
  const api = getApi();
  const response = await api.get<{ apiKeys: LoginResponse['apiKeys'] }>('/auth/keys');
  return response.data;
}

/**
 * 创建新的 API Key
 */
export async function createApiKey(name: string): Promise<{ key: string }> {
  const api = getApi();
  const response = await api.post<{ key: string }>('/auth/keys', { name });
  return response.data;
}

// ==================== 业务 API ====================

/**
 * 分析参考文本
 */
export async function analyzeText(text: string, title?: string): Promise<AnalyzeResponse> {
  const api = getApi();
  const llmConfig = getLLMConfig();

  const response = await api.post<ApiResponse<AnalyzeResponse>>('/analyze', {
    text,
    title,
    ...(llmConfig && {
      backend: llmConfig.backend,
      apiKey: llmConfig.apiKey,
      baseUrl: llmConfig.baseUrl,
      model: llmConfig.model,
    }),
  });

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '分析失败');
  }
  return response.data.data;
}

/**
 * 获取历史分析结果
 */
export async function getAnalysis(id: string): Promise<AnalyzeResponse> {
  const api = getApi();
  const response = await api.get<ApiResponse<AnalyzeResponse>>(`/analyze/${id}`);
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '获取失败');
  }
  return response.data.data;
}

/**
 * 生成大纲
 */
export async function generateOutline(
  request: GenerateOutlineRequest
): Promise<GenerateResponse> {
  const api = getApi();
  const llmConfig = getLLMConfig();

  if (!llmConfig) {
    throw new Error('请先在设置页面配置 LLM API Key');
  }

  const response = await api.post<ApiResponse<GenerateResponse>>('/generate', {
    ...request,
    backend: llmConfig.backend,
    apiKey: llmConfig.apiKey,
    baseUrl: llmConfig.baseUrl,
    model: llmConfig.model,
  });

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '生成失败');
  }
  return response.data.data;
}

/**
 * 获取已生成的大纲
 */
export async function getOutline(id: string): Promise<GenerateResponse> {
  const api = getApi();
  const response = await api.get<ApiResponse<GenerateResponse>>(`/generate/${id}`);
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '获取失败');
  }
  return response.data.data;
}

/**
 * 重新生成大纲
 */
export async function regenerateOutline(
  id: string,
  feedback: string
): Promise<GenerateResponse> {
  const api = getApi();
  const llmConfig = getLLMConfig();

  const response = await api.post<ApiResponse<GenerateResponse>>(
    `/generate/${id}/regenerate`,
    {
      feedback,
      ...(llmConfig && {
        backend: llmConfig.backend,
        apiKey: llmConfig.apiKey,
        baseUrl: llmConfig.baseUrl,
        model: llmConfig.model,
      }),
    }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '重新生成失败');
  }
  return response.data.data;
}

// ==================== V3 API ====================

interface V3UploadResponse {
  success: boolean;
  bookId: string;
  title: string;
  chapters: Array<{
    index: number;
    title: string;
    wordCount: number;
  }>;
  metadata: {
    totalChapters: number;
    totalWords: number;
    encoding: string;
  };
}

interface V3AnalyzeResponse {
  success: boolean;
  ruleSetId: string;
  status: string;
  rules: RuleSet;
  contextMode?: 'chapter_yaml' | 'raw_text';
  contextStats?: {
    sampledTextChapters: number;
    storedStructuredChapters: number;
    sampledStructuredChapters: number;
  };
}

interface V3GenerateResponse {
  success: boolean;
  outlineId: string;
  outline: {
    title: string;
    genre: string;
    synopsis: string;
    chapters: Array<{
      index: number;
      title: string;
      summary: string;
      wordCount: number;
      keyPoints: string[];
    }>;
  };
  goldenChapters: {
    chapter1: string;
    chapter2: string;
    chapter3: string;
  };
}

interface V3GenerateRequest {
  ruleSetId: string;
  userInput: {
    genre: string;
    desiredWords: number;
    style?: string;
    customRequirements?: string;
  };
  promptConfig?: PromptConfig;
}

/**
 * V3: 上传书籍文件（直接上传）
 */
export async function v3UploadBook(file: File, encoding: string = 'auto'): Promise<V3UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('encoding', encoding);

  const apiEndpoint = getApiBaseUrl();
  const response = await fetchWithRetry(`${apiEndpoint}/api/v3/upload`, {
    method: 'POST',
    body: formData,
  }, {
    timeoutMs: 10 * 60 * 1000,
    retries: 1,
    label: '上传文件',
  });

  const result = await parseJsonResponse<any>(response, '上传文件');
  if (!result.success) {
    throw new Error(result.error?.message || '上传失败');
  }
  return result;
}


/**
 * V3: 分片上传书籍文件（支持大文件）
 * 修复：添加 uploadId、chunkIndex、totalChunks 参数
 */
export async function v3UploadBookChunked(
  file: File,
  encoding: string = 'auto',
  onProgress?: (progress: number) => void
): Promise<V3UploadResponse> {
  const CHUNK_SIZE = 512 * 1024; // 512KB per chunk，兼顾请求次数和稳定性
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // Step 1: 初始化上传
  const initResponse = await fetchWithRetry(`${getApiBaseUrl()}/api/v3/upload/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: file.name,
      totalChunks,
      encoding,
      totalBytes: file.size,
    }),
  }, {
    timeoutMs: 60 * 1000,
    retries: 2,
    label: '初始化分片上传',
  });

  const initResult = await parseJsonResponse<any>(initResponse, '初始化分片上传');
  if (!initResult.success) {
    throw new Error(initResult.error?.message || '初始化上传失败');
  }

  const uploadId = initResult.uploadId;

  // Step 2: 串行上传分片（临时方案，避免并发竞争）
  let uploadedChunks = 0;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    const formData = new FormData();
    formData.append('chunk', chunk, `${file.name}.part${i}`);
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', String(i));
    formData.append('totalChunks', String(totalChunks));

    const chunkResponse = await fetchWithRetry(`${getApiBaseUrl()}/api/v3/upload/chunk`, {
      method: 'POST',
      body: formData,
    }, {
      timeoutMs: 120 * 1000,
      retries: 2,
      label: `上传第 ${i + 1}/${totalChunks} 个分片`,
    });

    const chunkResult = await parseJsonResponse<any>(chunkResponse, `上传第 ${i + 1}/${totalChunks} 个分片`);
    if (!chunkResult.success) {
      throw new Error(chunkResult.error?.message || `分片 ${i + 1} 上传失败`);
    }

    uploadedChunks++;
    // 报告进度
    if (onProgress) {
      onProgress(Math.round((uploadedChunks / totalChunks) * 80));
    }
  }
  
  // Step 3: 完成上传
  if (onProgress) {
    onProgress(90);
  }
  
  const completeResponse = await fetchWithRetry(`${getApiBaseUrl()}/api/v3/upload/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uploadId }),
  }, {
    timeoutMs: 180 * 1000,
    retries: 1,
    label: '完成上传',
  });
  
  const completeResult = await parseJsonResponse<any>(completeResponse, '完成上传');
  if (!completeResult.success) {
    throw new Error(completeResult.error?.message || '完成上传失败');
  }
  
  if (onProgress) {
    onProgress(100);
  }
  
  return completeResult;
}


/**
 * V3: 分析书籍提取规则
 */
export async function v3AnalyzeBook(
  bookId: string,
  options?: { fastMode?: boolean; sampleSize?: number },
  promptConfig?: PromptConfig
): Promise<V3AnalyzeResponse> {
  const api = getApi();
  
  try {
    const response = await api.post<any>('/v3/analyze', {
      bookId,
      options,
      promptConfig,
    });

    // V3 API 返回的数据直接在根级别，不是 response.data.data
    if (!response.data.success) {
      throw new Error(response.data.error?.message || '分析失败');
    }
    // 直接返回 response.data，因为 V3 API 不包装在 data 字段中
    return response.data;
  } catch (error: any) {
    console.error('v3AnalyzeBook error:', error);
    // 增强错误信息
    if (error.response?.data) {
      const data = error.response.data;
      if (data.error?.message) {
        throw new Error(data.error.message);
      }
    }
    throw error;
  }
}

export async function v3StartAnalyzeTask(request: {
  bookId: string;
  options?: { fastMode?: boolean; sampleSize?: number };
  promptConfig?: PromptConfig;
}): Promise<{
  success: boolean;
  taskId: string;
  status: 'pending' | 'processing' | 'completed';
  progressMessage?: string;
}> {
  const api = getApi();
  const response = await api.post<any>('/v3/analyze/task', request);
  if (!response.data.success) {
    throw new Error(response.data.error?.message || '创建拆书任务失败');
  }
  return response.data;
}

export function v3RunAnalyzeTask(taskId: string): void {
  const url = `${getApiBaseUrl()}/api/v3/analyze/tasks/${taskId}/run`;
  void fetch(url, {
    method: 'POST',
    headers: buildAuthHeaders(),
  }).catch((error) => {
    console.error('[V3 API] Run analyze task failed:', error);
  });
}

export async function v3GetAnalyzeTask(taskId: string): Promise<V3AnalyzeTask> {
  const api = getApi();
  const response = await api.get<any>(`/v3/analyze/tasks/${taskId}`);
  if (!response.data.success || !response.data.task) {
    throw new Error(response.data.error?.message || '获取拆书任务失败');
  }
  return response.data.task as V3AnalyzeTask;
}

/**
 * V3: 基于规则生成大纲
 */
export async function v3GenerateOutline(request: V3GenerateRequest): Promise<V3GenerateResponse> {
  const api = getApi();
  const response = await api.post<ApiResponse<V3GenerateResponse> | V3GenerateResponse>('/v3/generate', request);
  const wrapped = response.data as ApiResponse<V3GenerateResponse>;
  if (wrapped.data) {
    if (!wrapped.success) {
      throw new Error(wrapped.error?.message || '生成失败');
    }
    return wrapped.data;
  }

  const direct = response.data as V3GenerateResponse;
  if (!direct.success) {
    throw new Error('生成失败');
  }
  return direct;
}

interface V3DeepAnalyzeResponse {
  success: boolean;
  analysisId: string;
  bookId: string;
  totalChapters: number;
  analyzedChapters: number;
  status: string;
  chapters: Array<{
    chapter: number;
    title: string;
    summary: string;
    protagonist: string;
    realm: string;
    chapterTypes: string[];
    items: number;
    foreshadowing: { planted: number; harvested: number };
  }>;
}

interface V3ProgressResponse {
  success: boolean;
  bookId: string;
  totalChapters: number;
  analyzedChapters: number;
  status: string;
  overallSummary?: string;
  progress: number;
}

export interface V3ChapterListItem {
  chapter: number;
  title: string;
  summary: string;
  protagonist: string;
  realm: string;
  protagonistGoal?: string;
  protagonistGains?: string[];
  chapterTypes: string[];
  location?: {
    current?: string;
    entered?: boolean;
    explored?: number;
  };
  cliffhanger?: string;
  plotProgress?: {
    main?: string;
    romance?: string;
    revenge?: string;
  };
  facts?: ChapterFacts;
  items: any[];
  foreshadowing: {
    planted: any[];
    harvested: any[];
  };
}

export interface V3SummaryResponse {
  success: boolean;
  summary: {
    totalChapters: number;
    protagonistArc: Array<{
      chapter: number;
      realm: string;
      mental: string;
      gains: string[];
    }>;
    characters: any[];
    items: any[];
    locations: any[];
    foreshadowing: {
      total: number;
      planted: number;
      harvested: number;
      details: any[];
    };
    plotProgress: Array<{
      chapter: number;
      main: string;
      romance: string;
      revenge: string;
    }>;
  };
}

export async function v3DeepAnalyze(
  bookId: string,
  options?: { maxChapters?: number; startChapter?: number }
): Promise<V3DeepAnalyzeResponse> {
  const api = getApi();
  const response = await api.post<any>('/v3/analyze/deep', { bookId, options });
  if (!response.data.success) {
    throw new Error(response.data.error?.message || '深度分析失败');
  }
  return response.data;
}

export async function v3GetProgress(bookId: string): Promise<V3ProgressResponse> {
  const api = getApi();
  const response = await api.get<any>(`/v3/analyze/progress/${bookId}`);
  if (!response.data.success) {
    throw new Error(response.data.error?.message || '获取进度失败');
  }
  return response.data;
}

export async function v3GetChapters(bookId: string): Promise<{ success: boolean; chapters?: V3ChapterListItem[]; chapter?: any }> {
  const api = getApi();
  const response = await api.get<any>(`/v3/analyze/chapters/${bookId}`);
  if (!response.data.success) {
    throw new Error(response.data.error?.message || '获取章节失败');
  }
  return response.data;
}

export async function v3GetSummary(bookId: string): Promise<V3SummaryResponse> {
  const api = getApi();
  const response = await api.get<any>(`/v3/analyze/summary/${bookId}`);
  if (!response.data.success) {
    throw new Error(response.data.error?.message || '获取汇总失败');
  }
  return response.data;
}


// ========== V3 三层大纲生成 ==========

interface ThreeTierOutlineRequest {
  ruleSetId: string;
  userInput: {
    genre: string;
    desiredWords: number;
    chapterPlanMode?: ChapterPlanMode;
    style?: string;
    customRequirements?: string;
  };
  promptConfig?: PromptConfig;
}

interface ThreeTierOutline {
  totalOutline: {
    title: string;
    genre: string;
    synopsis: string;
    totalSummary: string;
    protagonist: string;
    coreConflict: string;
    ending: string;
  };
  volumes: Array<{
    number: number;
    title: string;
    coreEvent: string;
    summary: string;
    chapters: Array<{ index: number; title: string }>;
  }>;
  chapterOutlines: Array<{
    index: number;
    title: string;
    summary: string;
    keyPoints: string[];
  }>;
}

interface ThreeTierOutlineResponse {
  success: boolean;
  outlineId: string;
  outline: ThreeTierOutline;
  planningMeta?: {
    chapterPlanMode: ChapterPlanMode;
    sourceChapterCount?: number;
    plannedChapterCount: number;
    plannedVolumeCount: number;
    averageChapterWords: number;
  };
}

export type { ThreeTierOutline, ThreeTierOutlineResponse };

export interface V3GenerationTask {
  id: string;
  mode: 'standard' | 'three-tier' | 'repair-golden-chapters' | 'analyze';
  ruleSetId?: string;
  sourceOutlineId?: string;
  outlineId?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progressMessage?: string;
  errorMessage?: string;
  resultData?: any;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
}

export interface V3AnalyzeTask {
  id: string;
  mode: 'analyze';
  ruleSetId?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progressMessage?: string;
  errorMessage?: string;
  resultData?: V3AnalyzeResponse;
  requestPayload?: {
    bookId: string;
    options?: { fastMode?: boolean; sampleSize?: number };
    promptConfig?: PromptConfig;
  };
  isStale?: boolean;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
}

export type HistoryOutlineType = 'standard' | 'three-tier' | 'unknown';

export interface V3RuleSetSnapshot {
  id: string;
  sourceBookId: string;
  sourceBookTitle: string;
  status: string;
  analyzedChapters: number;
  createdAt: string;
  rules: RuleSet;
}

export interface V3DeepAnalysisSnapshot {
  sourceBookId: string;
  sourceBookTitle: string;
  status: string;
  totalChapters: number;
  analyzedChapters: number;
  overallSummary?: string | null;
  charactersCount: number;
  itemsCount: number;
  locationsCount: number;
  protagonistArc: Array<{
    chapter: number;
    realm: string;
    mental: string;
    gains: string[];
  }>;
  plotProgress: Array<{
    chapter: number;
    main: string;
    romance: string;
    revenge: string;
  }>;
  characters: Array<{
    name: string;
    role?: string;
    description?: string;
    relationshipToProtagonist?: string;
  }>;
  items: Array<{
    name: string;
    type?: string;
    rarity?: string;
    owner?: string;
    chapterAppeared?: number;
  }>;
  locations: Array<{
    name: string;
    type?: string;
    description?: string;
    chapterUnlocked?: number;
    importance?: string;
  }>;
  foreshadowing: {
    total: number;
    planted: number;
    harvested: number;
    details: Array<{
      content: string;
      status: string;
      chapterPlanted?: number;
      chapterHarvested?: number;
      importance?: string;
      category?: string;
    }>;
  };
}

export interface V3HistoryOutlineResponse {
  id: string;
  type: HistoryOutlineType;
  title: string;
  synopsis: string;
  chapterCount: number;
  plannedChapterCount?: number;
  sourceChapterCount?: number | null;
  volumeCount: number;
  chapterPlanMode?: ChapterPlanMode | null;
  chapterPlanModeInferred?: boolean;
  hasGoldenChapters: boolean;
  model?: string | null;
  ruleSetId?: string | null;
  promptConfig?: PromptConfig | null;
  standardOutline?: {
    title: string;
    genre: string;
    synopsis: string;
    chapters: Array<{
      index: number;
      title: string;
      summary: string;
      wordCount: number;
      keyPoints?: string[];
    }>;
  } | null;
  threeTierOutline?: ThreeTierOutline | null;
  goldenChapters?: {
    chapter1?: string;
    chapter2?: string;
    chapter3?: string;
  } | null;
  rawOutline?: any;
  ruleSet?: V3RuleSetSnapshot | null;
  deepAnalysis?: V3DeepAnalysisSnapshot | null;
  genre: string;
  targetWordCount: number;
  createdAt: string;
}


export async function v3GenerateThreeTierOutline(
  request: ThreeTierOutlineRequest
): Promise<ThreeTierOutlineResponse> {
  const api = getApi();
  const response = await api.post<ApiResponse<ThreeTierOutlineResponse> | ThreeTierOutlineResponse>(
    '/v3/generate/three-tier',
    request
  );
  const wrapped = response.data as ApiResponse<ThreeTierOutlineResponse>;
  if (wrapped.data) {
    if (!wrapped.success) {
      throw new Error(wrapped.error?.message || '三层大纲生成失败');
    }
    return wrapped.data;
  }

  const direct = response.data as ThreeTierOutlineResponse;
  if (!direct.success) {
    throw new Error('三层大纲生成失败');
  }
  return direct;
}

export async function v3StartGenerationTask(request: {
  mode: 'standard' | 'three-tier';
  ruleSetId: string;
  userInput: {
    genre: string;
    desiredWords: number;
    chapterPlanMode?: ChapterPlanMode;
    style?: string;
    customRequirements?: string;
  };
  promptConfig?: PromptConfig;
}): Promise<{
  success: boolean;
  taskId: string;
  status: 'pending' | 'processing';
  progressMessage?: string;
}> {
  const api = getApi();
  const response = await api.post<any>('/v3/generate/task', request);
  if (!response.data.success) {
    throw new Error(response.data.error?.message || '创建生成任务失败');
  }
  return response.data;
}

export function v3RunGenerationTask(taskId: string): void {
  const url = `${getApiBaseUrl()}/api/v3/generate/tasks/${taskId}/run`;
  void fetch(url, {
    method: 'POST',
    headers: buildAuthHeaders(),
  }).catch((error) => {
    console.error('[V3 API] Run generation task failed:', error);
  });
}

export async function v3GetGenerationTask(taskId: string): Promise<V3GenerationTask> {
  const api = getApi();
  const response = await api.get<any>(`/v3/generate/tasks/${taskId}`);
  if (!response.data.success || !response.data.task) {
    throw new Error(response.data.error?.message || '获取生成任务失败');
  }
  return response.data.task as V3GenerationTask;
}

export async function v3StartRepairTask(outlineId: string): Promise<{
  success: boolean;
  taskId: string;
  status: 'pending' | 'processing';
  progressMessage?: string;
}> {
  const api = getApi();
  const response = await api.post<any>(`/v3/generate/repair/${outlineId}`);
  if (!response.data.success) {
    throw new Error(response.data.error?.message || '创建历史修复任务失败');
  }
  return response.data;
}

export async function getV3HistoryOutline(id: string): Promise<V3HistoryOutlineResponse> {
  const api = getApi();
  const response = await api.get<any>(`/outlines/${id}`);
  if (!response.data.success || !response.data.outline) {
    throw new Error(response.data.error?.message || '获取历史大纲失败');
  }
  return response.data.outline as V3HistoryOutlineResponse;
}

export default getApi();
